import { useEffect, useState } from "react";
import { Alert, AlertIcon, AlertTitle, AlertDescription, Box } from "@chakra-ui/react";
import { fetchPendingAttributionCount } from "./fetchPendingAttributionCount";

/**
 * Surfaces transactions that synced without card attribution.
 *
 * These rows are invisible everywhere else in the ERP: the Cardholders tab
 * excludes them, Auto Match never considers them, and cardholder views
 * cannot retrieve them. Without this notice they accumulate silently, which
 * is how eleven days of missing transactions went unnoticed in July.
 *
 * Renders nothing when there is nothing to report, so it stays out of the
 * way during normal operation.
 */
export const PendingAttributionNotice = () => {
  const [result, setResult] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const data = await fetchPendingAttributionCount();
      if (!cancelled) {
        setResult(data);
        setLoaded(true);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loaded) return null;

  // A failed query is not the same as zero pending rows. Saying nothing
  // when we genuinely do not know would recreate the silent-failure
  // problem this component exists to prevent.
  if (result === null) {
    return (
      <Alert status="warning" borderRadius="md" mb={4}>
        <AlertIcon />
        <Box>
          <AlertTitle>Attribution check unavailable</AlertTitle>
          <AlertDescription>
            Could not determine whether any transactions are awaiting card
            attribution. This is a system issue, not a data issue.
          </AlertDescription>
        </Box>
      </Alert>
    );
  }

  const { count, oldestDate, truncated } = result;

  if (count === 0) return null;

  return (
    <Alert status="warning" borderRadius="md" mb={4}>
      <AlertIcon />
      <Box>
        <AlertTitle>
          {count}
          {truncated ? "+" : ""} transaction{count === 1 ? "" : "s"} awaiting
          card attribution
        </AlertTitle>
        <AlertDescription>
          Transactions were synced and one or more transactions were 
          missing card attribution. Contact webadmin to resolve this issue.
          {oldestDate ? ` Oldest is dated ${oldestDate}.` : ""}
        </AlertDescription>
      </Box>
    </Alert>
  );
};